import React, { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUpRight,
  BarChart3,
  DollarSign,
  ExternalLink,
  FileText,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { formatCompactCurrency, formatCompactNumber, formatDate } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { ProjectAnalyticsProps } from './ProjectAnalytics';
import ProjectChart from './ProjectChart';

type StatRowSize = 'sm' | 'md';

interface AnalyticsStat {
  label: string;
  value: string;
  icon: React.ReactNode;
  withMutedDollar?: boolean;
  isPriceChange?: boolean;
  size: StatRowSize;
}

const extractEnumValue = (value: string): string => {
  if (!value) return value;
  if (value.includes('.')) return value.split('.').pop() || value;
  return value;
};

const formatDealType = (dealType: string): string => {
  const value = extractEnumValue(dealType);
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ProjectAnalyticsMobile: React.FC<ProjectAnalyticsProps> = ({
  graphData,
  project,
  loading = false,
}) => {
  const [showAllFundingRounds, setShowAllFundingRounds] = useState(false);

  if (!graphData) {
    return (
      <div className="py-12 text-center">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No analytics data available
        </p>
      </div>
    );
  }

  const totalOrders = graphData.orders?.length || 0;
  const totalFundingRounds = graphData.funding_rounds?.length || 0;
  const totalOrderValue = graphData.orders?.reduce((sum, order) => sum + order.offered_amount, 0) || 0;
  const totalFundingRaised = graphData.funding_rounds?.reduce((sum, round) => sum + (round.amount_raised || 0), 0) || 0;
  const latestPrice = graphData.price_history?.[graphData.price_history.length - 1];
  const firstPrice = graphData.price_history?.[0];

  const priceChange = latestPrice && firstPrice
    ? ((latestPrice.price_usd - firstPrice.price_usd) / firstPrice.price_usd) * 100
    : null;

  const renderCurrencyValue = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('$')) return trimmedValue;

    return (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {trimmedValue.slice(1)}
      </>
    );
  };

  const renderPriceChangeValue = (value: string) => {
    if (value === '—') return value;

    const isNegative = value.startsWith('-');
    const isPositive = value.startsWith('+');
    const numericPart = value.replace('%', '').replace('+', '').replace('-', '');
    const sign = isNegative ? '-' : isPositive ? '+' : '';
    const colorClass = isNegative ? 'text-[#ff540c]' : 'text-[#1fb845]';

    return (
      <>
        <span className={`text-[23px] leading-[32px] tracking-[-0.2px] ${colorClass}`}>
          {sign}
          {numericPart}
        </span>
        <span className={`text-[14px] leading-[32px] ${colorClass}`}>%</span>
      </>
    );
  };

  const renderStatValue = (stat: AnalyticsStat) => {
    if (stat.isPriceChange) return renderPriceChangeValue(stat.value);
    if (stat.withMutedDollar) return renderCurrencyValue(stat.value);
    return stat.value;
  };

  const stats: AnalyticsStat[] = [
    {
      label: 'Total Orders',
      value: formatCompactNumber(totalOrders),
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      size: 'sm',
    },
    {
      label: 'Total Order Value',
      value: formatCompactCurrency(totalOrderValue),
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      size: 'sm',
    },
    {
      label: 'Funding\nRounds',
      value: formatCompactNumber(totalFundingRounds),
      icon: <TrendingUp size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      size: 'md',
    },
    {
      label: 'Total Funding\nRaised',
      value: formatCompactCurrency(totalFundingRaised),
      icon: <ArrowUpRight size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      size: 'md',
    },
    {
      label: 'Current Price',
      value: latestPrice ? `$${latestPrice.price_usd.toFixed(2)}` : '—',
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      size: 'sm',
    },
    {
      label: 'Market Cap',
      value: latestPrice ? formatCompactCurrency(latestPrice.market_cap_usd) : '—',
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      size: 'sm',
    },
    {
      label: 'FDV',
      value: latestPrice ? formatCompactCurrency(latestPrice.spot_fdv_usd) : '—',
      icon: <FileText size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      size: 'sm',
    },
    {
      label: 'Price Change',
      value: priceChange !== null ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%` : '—',
      icon: priceChange !== null && priceChange < 0
        ? <TrendingDown size={16} className="text-[#ff540c]" />
        : <TrendingUp size={16} className={priceChange !== null ? 'text-[#1fb845]' : 'text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'} />,
      isPriceChange: true,
      size: 'sm',
    },
  ];

  const fundingRounds = useMemo(() => {
    const rounds = project?.rounds || [];
    return [...rounds].sort((a, b) => {
      const aTime = new Date(a.date || '').getTime();
      const bTime = new Date(b.date || '').getTime();
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return -1;
      if (Number.isNaN(bTime)) return 1;
      return bTime - aTime;
    });
  }, [project?.rounds]);

  const visibleFundingRounds = showAllFundingRounds ? fundingRounds : fundingRounds.slice(0, 4);
  const hasMoreFundingRounds = fundingRounds.length > 4;

  const recentOrders = useMemo(() => {
    const orders = graphData.orders || [];
    return [...orders]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 9);
  }, [graphData.orders]);

  const rowHeights = ['h-[108px]', 'h-[130px]', 'h-[112px]', 'h-[112px]'];
  const statRows = [
    stats.slice(0, 2),
    stats.slice(2, 4),
    stats.slice(4, 6),
    stats.slice(6, 8),
  ];

  return (
    <div className="space-y-7">
      <div>
        <h2 className="mb-4 font-['Plus_Jakarta_Sans'] text-[23px] font-semibold leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Project Valuation Chart
        </h2>
        <ProjectChart graphData={graphData} loading={loading} variant="mobile" />
      </div>

      <div className="border border-white bg-[rgba(255,255,255,0.32)] p-5 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <h2 className="font-['Plus_Jakarta_Sans'] text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Analytics Overview
        </h2>

        {loading ? (
          <div className="mt-5 grid grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-[108px] bg-white p-4 dark:bg-[#1c1c1c]">
                <Skeleton className="mb-3 h-4 w-20" />
                <Skeleton className="h-7 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {statRows.map((row, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-2 gap-4">
                {row.map((stat) => (
                  <div
                    key={stat.label}
                    className={`${rowHeights[rowIndex]} flex items-center bg-white p-4 dark:bg-[#1c1c1c]`}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-[6px]">
                        {stat.icon}
                        <p className="whitespace-pre-line font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          {stat.label}
                        </p>
                      </div>
                      <p
                        className={`font-['Plus_Jakarta_Sans'] font-medium tracking-[-0.2px] ${
                          stat.isPriceChange
                            ? 'text-[0px] leading-[0px]'
                            : 'text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd]'
                        }`}
                      >
                        {renderStatValue(stat)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-white bg-[rgba(255,255,255,0.32)] p-5 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <h2 className="font-['Plus_Jakarta_Sans'] text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Funding History
        </h2>

        {loading ? (
          <div className="mt-5 space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
                <Skeleton className="mb-3 h-7 w-40" />
                <Skeleton className="h-5 w-28" />
              </div>
            ))}
          </div>
        ) : fundingRounds.length > 0 ? (
          <div className="mt-5 space-y-3">
            {visibleFundingRounds.map((round, index) => {
              const showAmountRaised = (round.amount_raised || 0) > 0;
              const showValuation = (round.fully_diluted_valuation || 0) > 0;
              const showInvestors = round.investors && round.investors.length > 0;
              const roundTitle = round.round_name || 'Unknown';

              return (
                <div
                  key={`${round.round_name}-${round.date}-${index}`}
                  className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex min-w-0 items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#e3fd5a]">
                        <BarChart3 size={24} className="text-[#2d2923]" />
                      </div>
                      <div>
                        <p className="font-['Plus_Jakarta_Sans'] text-[16px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                          {roundTitle}
                        </p>
                        <p className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          {round.date ? formatDate(round.date) : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      {showAmountRaised && (
                        <p className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                          {renderCurrencyValue(formatCompactCurrency(round.amount_raised || 0))}
                        </p>
                      )}
                      {showValuation && (
                        <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          Valuation: {formatCompactCurrency(round.fully_diluted_valuation || 0)}
                        </p>
                      )}
                    </div>
                  </div>

                  {showInvestors && (
                    <>
                      <div className="my-4 border-t border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)]" />

                      <div className="mb-3 flex items-center gap-[6px]">
                        <ExternalLink size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                        <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          Total Investors
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {round.investors.map((investor, investorIndex) => (
                          <div
                            key={`${investor}-${investorIndex}`}
                            className="flex items-center gap-[6px] border border-[rgba(45,41,35,0.12)] bg-[rgba(45,41,35,0.04)] pl-3 pr-4 py-[6px] dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(253,253,253,0.04)]"
                          >
                            <ExternalLink size={10} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
                            <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                              {investor}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {hasMoreFundingRounds && (
              <div className="mt-2 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllFundingRounds((prev) => !prev)}
                  className="flex items-center gap-1 bg-[rgba(255,255,255,0.8)] px-4 py-2 font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[#2d2923] transition-opacity hover:opacity-90 dark:bg-[rgba(28,28,28,0.8)] dark:text-[#fdfdfd]"
                >
                  {showAllFundingRounds ? 'Show less' : 'Show more'}
                  <ArrowDown size={16} className={showAllFundingRounds ? 'rotate-180' : ''} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-5 font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            No funding history available
          </p>
        )}
      </div>

      <div className="border border-white bg-[rgba(255,255,255,0.32)] p-5 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <h2 className="font-['Plus_Jakarta_Sans'] text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Recent Orders
        </h2>

        {loading ? (
          <div className="mt-5 space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-[78px] border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
                <Skeleton className="mb-2 h-5 w-28" />
                <Skeleton className="h-4 w-40" />
              </div>
            ))}
          </div>
        ) : recentOrders.length > 0 ? (
          <div className="mt-5 space-y-3">
            {recentOrders.map((order, index) => {
              const orderType = extractEnumValue(order.type);
              const dealType = formatDealType(order.deal_type);
              const isBuy = orderType.includes('BUY') || orderType === 'BUY';

              return (
                <div
                  key={`${order.order_id}-${index}`}
                  className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-[14px]">
                        <div
                          className={`flex h-6 min-w-[74px] items-center justify-center border px-2 font-['Plus_Jakarta_Sans'] text-[12px] font-semibold leading-[16px] ${
                            isBuy
                              ? 'border-[#1fb845] text-[#1fb845]'
                              : 'border-[#ff540c] text-[#ff540c]'
                          }`}
                        >
                          {isBuy ? 'BUY' : 'SELL'}
                        </div>
                        <span className="font-['Plus_Jakarta_Sans'] text-[12px] font-semibold leading-[16px] text-[#2d2923] dark:text-[#fdfdfd]">
                          {dealType}
                        </span>
                      </div>

                      <p className="mt-[6px] font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        {formatDate(order.date)} • {order.round || 'Unknown'}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {renderCurrencyValue(formatCompactCurrency(order.offered_amount))}
                      </p>
                      <p className="mt-[6px] font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        FDV: {formatCompactCurrency(order.offered_fully_diluted_valuation)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-5 font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            No orders available
          </p>
        )}
      </div>
    </div>
  );
};

export default ProjectAnalyticsMobile;
